/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Box,
  Checkbox,
  CircularProgress,
  ListItemText,
  Modal,
  styled,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EntriesFilterRow from 'components/Monitor/ResultsTab/TableFilters/EntriesFilterRow';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import {
  CustomLabel,
  CustomSwitch,
} from 'components/Common/MUIStyledComponents';
import { enqueueSnackbar } from 'notistack';
import BiteDBAPI from 'api/BiteDBAPI';
import { useInvalidateAllDatabaseKeys } from 'api/bitedb/database/database.hooks';
import {
  resolveBeneficialOwnershipEnabled,
  useEntityScreeningLenses,
  withBeneficialOwnershipCompatibility,
  withoutBeneficialOwnership,
} from 'hooks/useEntityScreeningLenses';
import DatabaseEmailAudienceEditor from './DatabaseEmailAudienceEditor';

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--card-text)',
  },
  overflow: 'hidden',
  color: 'var(--Text-Main, var(--text-primary))',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  borderRadius: '8px',
  border: '1px solid var(--bg-secondary)',
});

const AIDisclaimerBox = styled(Box)({
  backgroundColor: 'var(--bg-products',
  border: '1px solid #3E424B',
  borderRadius: '8px',
  padding: '16px',
  marginTop: '32px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
});

interface DashboardProps {
  database: any;
}

const ScreeningSettings: React.FC<DashboardProps> = ({ database }) => {
  const { cur_user } = React.useContext(GlobalContext);
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();
  const [frequency, setFrequency] = useState<string>('-');
  const [weekDay, setWeekDay] = useState<string>('-');
  const [riskThreshold, setRiskThreshold] = useState<string>('Medium');
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [isNotification, setIsNotification] = useState(false);
  const [isEntityAddonEnabled, setIsEntityAddonEnabled] = useState(false);
  const [isBeneficialOwnershipEnabled, setIsBeneficialOwnershipEnabled] =
    useState(false);
  const [selectedOptsType, setSelectedOptsType] = useState([]);
  const [savingDatabases, setSavingDatabases] = React.useState(false);
  const [loadingDatabase, setLoadingDatabase] = React.useState(false);
  const [databaseData, setDatabaseData] = React.useState<any>(null);
  const [usesAllAvailableLists, setUsesAllAvailableLists] = useState(false);
  const isEntityDatabase = database?.type === 'people & companies';
  const { lenses: entityScreeningLenses, isLoading: isLoadingLenses } =
    useEntityScreeningLenses(isEntityDatabase);
  const canAccessEntityAddon = Boolean(cur_user?.hasEntityAddon);
  const isTeamAdmin = Boolean(
    cur_user?.selected_team?.attributes?.team_admin?.includes(
      cur_user?.basics?.email,
    ) || cur_user?.role === 'owner',
  );
  const canManageEntityAddon =
    database?.type === 'people & companies' &&
    canAccessEntityAddon &&
    isTeamAdmin;

  // Screening lists this database type can be screened against; the user picks
  // which ones apply and the selection is persisted on save (screening_lists).
  const entityAvailableLists = useMemo(
    () =>
      Array.from(
        new Set([
          ...entityScreeningLenses,
          ...withoutBeneficialOwnership(selectedLists),
        ]),
      ),
    [entityScreeningLenses, selectedLists],
  );
  const availableLists =
    database.type === 'products'
      ? [
          'AD CVD Commodities',
          'AD CVD Entities',
          'Consumer Protection',
          'ESG Commodities',
          'Forced Labor Commodities',
          'Nomenclatures - HTS Codes Schedule B and CN Codes',
          'PGA Codes',
          'Sensitive Technology',
          'US Duties & Tariffs',
          'US Monitoring and Regulations',
        ]
      : entityAvailableLists;

  useEffect(() => {
    if (isEntityDatabase && usesAllAvailableLists) {
      setSelectedLists(entityScreeningLenses);
    }
  }, [entityScreeningLenses, isEntityDatabase, usesAllAvailableLists]);

  // Fetch database data on component mount
  useEffect(() => {
    const fetchDatabaseData = async () => {
      if (database?.id) {
        setLoadingDatabase(true);
        try {
          const response = await BiteDBAPI.getDatabaseById(
            database.id.toString(),
          );
          if (response?.success && response?.result?.data) {
            const dbData = response.result.data;
            setDatabaseData(dbData);

            // Populate form fields with database data
            if (dbData.screening_frequency) {
              setFrequency(dbData.screening_frequency);
            }
            if (dbData.screening_day_of_week) {
              setWeekDay(dbData.screening_day_of_week);
            }
            if (dbData.accepted_risk_threshold) {
              setRiskThreshold(dbData.accepted_risk_threshold);
            }
            const savedScreeningLists = Array.isArray(dbData.screening_lists)
              ? withoutBeneficialOwnership(dbData.screening_lists)
              : null;
            setIsBeneficialOwnershipEnabled(
              isEntityDatabase &&
                resolveBeneficialOwnershipEnabled(
                  dbData.screening_lists,
                  dbData.beneficial_ownership_enabled,
                ),
            );

            if (!isEntityDatabase) {
              if (dbData.screening_lists) {
                setSelectedLists(dbData.screening_lists || []);
              } else {
                setSelectedLists(availableLists);
              }
            } else if (
              Array.isArray(dbData.screening_lists) &&
              dbData.screening_lists.length > 0
            ) {
              setSelectedLists(savedScreeningLists ?? []);
              setUsesAllAvailableLists(false);
            } else {
              setUsesAllAvailableLists(true);
            }
            if (dbData.notifications_email) {
              const emailArray = dbData.notifications_email
                .split(',')
                .filter((email: string) => email.trim());
              setSelectedOptsType(emailArray);
            }
            if (dbData.notifications_enabled !== undefined) {
              setIsNotification(dbData.notifications_enabled);
            }
            if (typeof dbData.entity_add_on_enabled === 'boolean') {
              setIsEntityAddonEnabled(dbData.entity_add_on_enabled);
            } else {
              setIsEntityAddonEnabled(false);
            }
          } else {
            enqueueSnackbar('Failed to load database settings', {
              variant: 'error',
            });
          }
        } catch (error) {
          console.error('Error fetching database data:', error);
          enqueueSnackbar('Error loading database settings', {
            variant: 'error',
          });
        } finally {
          setLoadingDatabase(false);
        }
      }
    };

    fetchDatabaseData();
  }, [database?.id]);

  useEffect(() => {
    if (selectedOptsType.length > 0) {
      setIsNotification(true);
    } else {
      setIsNotification(false);
    }
  }, [selectedOptsType]);

  useEffect(() => {
    const notificationCount = database.notifications_email
      ? database.notifications_email.split(',').filter(Boolean).length
      : 0;
    setIsNotification(notificationCount > 0);
    if (database.notifications_email) {
      const opts = database.notifications_email
        .split(',')
        .map((opt: string) => opt.trim())
        .filter(Boolean);

      setSelectedOptsType(opts);
    } else {
      setSelectedOptsType([]); // no notifications
    }
  }, [database]);

  const handleFrequencyChange = (event: SelectChangeEvent<unknown>) => {
    setFrequency(event.target.value as string);
  };

  const handleWeekDayChange = (event: SelectChangeEvent<unknown>) => {
    setWeekDay(event.target.value as string);
  };

  const handleListsChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    // MUI gives a comma-joined string for autofill; otherwise it's the array.
    if (isEntityDatabase) {
      setUsesAllAvailableLists(false);
    }
    setSelectedLists(
      withoutBeneficialOwnership(
        typeof value === 'string' ? value.split(',') : (value as string[]),
      ),
    );
  };

  const handleSaveSettings = async () => {
    try {
      const ids = [database.id];
      const watchlistSelections = isEntityDatabase
        ? withoutBeneficialOwnership(selectedLists)
        : selectedLists;
      if (
        watchlistSelections.length === 0 &&
        (!isEntityDatabase || !isBeneficialOwnershipEnabled)
      ) {
        enqueueSnackbar(
          'Select a screening list or enable Beneficial Ownership',
          {
            variant: 'error',
          },
        );
        return;
      }
      setSavingDatabases(true);
      const settingsData = {
        last_updated: Date.now().toString(),
        screening_frequency: frequency,
        screening_day_of_week: weekDay,
        notifications_email: `${selectedOptsType}`,
        notifications_enabled: isNotification,
        entity_add_on_enabled: isEntityAddonEnabled,
        accepted_risk_threshold: riskThreshold,
        screening_lists: watchlistSelections,
        ids: ids,
        ...(isEntityDatabase
          ? {
              beneficial_ownership_enabled: isBeneficialOwnershipEnabled,
              screening_lists: withBeneficialOwnershipCompatibility(
                watchlistSelections,
                isBeneficialOwnershipEnabled,
              ),
            }
          : {}),
      };

      if (!settingsData.ids) {
        enqueueSnackbar('Please select at least one database', {
          variant: 'error',
        });
        return;
      }

      const response = await BiteDBAPI.updateDatabases(settingsData);
      if (response?.success) {
        setSavingDatabases(false);
        invalidateAllDatabaseKeys();
        enqueueSnackbar('Settings saved successfully', {
          variant: 'success',
        });
      } else {
        setSavingDatabases(false);
        enqueueSnackbar(`Error updating settings: ${response?.result}`, {
          variant: 'error',
        });
      }
    } catch (e) {
      setSavingDatabases(false);
      console.error(e);
      enqueueSnackbar(`Error updating settings: ${e}`, {
        variant: 'error',
      });
    }
  };

  const ticketType = [
    {
      id: 1,
      name: 'Email Notification',
    },
    {
      id: 2,
      name: 'In-app Notification',
    },
    {
      id: 3,
      name: 'Daily Updates Notification',
    },
  ];

  return (
    <>
      <div className='screening-settings'>
        {loadingDatabase ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <CircularProgress
              style={{ width: '40px', height: '40px' }}
              color='primary'
            />
          </div>
        ) : (
          <>
            <div className='screening-settings-col' style={{ gap: '0px' }}>
              <h2>
                <SettingsOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                />{' '}
                Database Settings
              </h2>
              <p className='subtitle'>
                Configure automated screening for Global Suppliers
              </p>
            </div>

            <div className='screening-settings-col'>
              <div className='hit-title'>
                <ListAltIcon sx={{ fontSize: 14 }} /> Screening Lists
              </div>
              <div className='screening-settings-row'>
                <div
                  className='audit-add-modal-cont-body-frame-form'
                  style={{ width: '100%' }}
                >
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                          Select the lists these{' '}
                          {database.type === 'products'
                            ? 'products'
                            : 'entities'}{' '}
                          are screened against
                        </div>
                        <FormControl sx={{ width: '100%' }}>
                          <CustomSelect
                            multiple
                            value={selectedLists}
                            onChange={handleListsChange}
                            fullWidth
                            IconComponent={KeyboardArrowDownIcon}
                            displayEmpty
                            renderValue={(selected) => {
                              const vals = (selected as string[]) ?? [];
                              if (vals.length === 0) {
                                return 'Select lists';
                              }
                              if (vals.length === availableLists.length) {
                                return 'All lists';
                              }
                              return `${vals.length} of ${availableLists.length} selected`;
                            }}
                            inputProps={{
                              MenuProps: {
                                MenuListProps: {
                                  sx: {
                                    backgroundColor: 'var(--bg-colors)',
                                    color:
                                      'var(--Text-Main, var(--text-primary))',
                                  },
                                },
                              },
                            }}
                          >
                            {isEntityDatabase && isLoadingLenses && (
                              <MenuItem disabled>
                                Loading the latest lenses…
                              </MenuItem>
                            )}
                            {availableLists.map((list) => (
                              <MenuItem key={list} value={list}>
                                <Checkbox
                                  checked={selectedLists.includes(list)}
                                  sx={{
                                    color: 'var(--Primary-400, #4895ef)',
                                    '&.Mui-checked': {
                                      color: 'var(--Primary-400, #4895ef)',
                                    },
                                  }}
                                />
                                <ListItemText primary={list} />
                              </MenuItem>
                            ))}
                          </CustomSelect>
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isEntityDatabase && (
                <div className='screening-settings-row screening-settings-ownership-section'>
                  <div className='hit-main' style={{ width: '100%' }}>
                    <div className='hit-title'>
                      <GroupsOutlinedIcon sx={{ fontSize: 16 }} /> Beneficial
                      Ownership
                    </div>
                    <div className='hit-meta'>
                      Screen this database against BITE&apos;s dedicated
                      beneficial-ownership and PEP records. This runs separately
                      from the selected BITE Lenses above.
                    </div>
                    <CustomLabel
                      control={
                        <CustomSwitch
                          checked={isBeneficialOwnershipEnabled}
                          onChange={(event: any) =>
                            setIsBeneficialOwnershipEnabled(
                              event.target.checked,
                            )
                          }
                          inputProps={{
                            'aria-label':
                              'Enable Beneficial Ownership screening',
                          }}
                        />
                      }
                      label={
                        isBeneficialOwnershipEnabled
                          ? 'Enabled for this database'
                          : 'Disabled for this database'
                      }
                      labelPlacement='start'
                      style={{
                        marginTop: '12px',
                        marginLeft: 0,
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* AI Disclaimer */}
              <AIDisclaimerBox>
                <div className='screening-settings-ai-notice-content'>
                  <div className={'hit-title'}>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 18, color: '#ffc107', marginTop: '2px' }}
                    />
                    AI Screening Notice
                  </div>
                  <div
                    className={
                      'audit-add-modal-cont-body-frame-form-row-input-label-text'
                    }
                  >
                    AI-powered screening checks are automatically disabled for
                    results containing more than 250 hits.
                  </div>
                </div>
              </AIDisclaimerBox>
            </div>

            <div className='screening-divider'></div>

            <div className='screening-settings-col'>
              <div className='hit-title'>
                <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} /> Screening
                Frequency
              </div>
              <div className='screening-settings-row'>
                <div
                  className='audit-add-modal-cont-body-frame-form'
                  style={{ width: '100%' }}
                >
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                          Frequency
                        </div>
                        <FormControl sx={{ width: '100%', height: '48px' }}>
                          <CustomSelect
                            value={frequency}
                            onChange={handleFrequencyChange}
                            fullWidth
                            IconComponent={KeyboardArrowDownIcon}
                            inputProps={{
                              MenuProps: {
                                MenuListProps: {
                                  sx: {
                                    backgroundColor: 'var(--bg-colors)',
                                    color:
                                      'var(--Text-Main, var(--text-primary))',
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value={'Once'}>Once</MenuItem>
                            <MenuItem value={'Daily'}>Daily</MenuItem>
                            <MenuItem value={'Weekly'}>Weekly</MenuItem>
                            <MenuItem value={'Monthly'}>Monthly</MenuItem>
                            <MenuItem value={'Yearly'}>Yearly</MenuItem>
                          </CustomSelect>
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
                {frequency === 'Weekly' && (
                  <div
                    className='audit-add-modal-cont-body-frame-form'
                    style={{ width: '100%' }}
                  >
                    <div className='audit-add-modal-cont-body-frame-form-row'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input'>
                        <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                          <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                            Day of Week
                          </div>
                          <FormControl sx={{ width: '100%', height: '48px' }}>
                            <CustomSelect
                              value={weekDay}
                              onChange={handleWeekDayChange}
                              fullWidth
                              IconComponent={KeyboardArrowDownIcon}
                              inputProps={{
                                MenuProps: {
                                  MenuListProps: {
                                    sx: {
                                      backgroundColor: 'var(--bg-colors)',
                                      color:
                                        'var(--Text-Main, var(--text-primary))',
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value={'Monday'}>Monday</MenuItem>
                              <MenuItem value={'Tuesday'}>Tuesday</MenuItem>
                              <MenuItem value={'Wednesday'}>Wednesday</MenuItem>
                              <MenuItem value={'Thursday'}>Thursday</MenuItem>
                              <MenuItem value={'Friday'}>Friday</MenuItem>
                              <MenuItem value={'Saturday'}>Saturday</MenuItem>
                              <MenuItem value={'Sunday'}>Sunday</MenuItem>
                            </CustomSelect>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='screening-divider'></div>

            <div className='screening-settings-col'>
              <div className='hit-main'>
                <div className='hit-title'>
                  <MarkunreadOutlinedIcon sx={{ fontSize: 14 }} />
                  Notifications
                </div>
              </div>
              <div className='screening-settings-row'>
                <div className='hit-main'>
                  <div className='hit-title'>
                    <strong>
                      Send Email Notifications / In-App Notification
                    </strong>
                  </div>
                  <div className='hit-meta'>
                    Get notified when screening results are available
                  </div>
                  {ticketType.map((row: any, index: number) => (
                    <EntriesFilterRow
                      key={index}
                      name={row.name}
                      selectedItems={selectedOptsType}
                      setSelectedItems={setSelectedOptsType}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className='screening-divider'></div>

            {isEntityDatabase && database?.id && (
              <>
                <div className='screening-settings-col'>
                  <DatabaseEmailAudienceEditor
                    databaseId={database.id.toString()}
                  />
                </div>
                <div className='screening-divider'></div>
              </>
            )}

            {canManageEntityAddon && (
              <>
                <div className='screening-settings-col'>
                  <div className='hit-main'>
                    <div className='hit-title'>
                      <DomainVerificationIcon sx={{ fontSize: 14 }} /> Business
                      Verification
                    </div>
                  </div>
                  <div className='screening-settings-row'>
                    <div className='hit-main'>
                      <div className='hit-title'>
                        <strong>
                          Run business verification on this database.
                        </strong>
                      </div>
                      <div className='hit-meta'>
                        Controls whether business verification checks run on the
                        records in this database.
                      </div>
                      <CustomLabel
                        control={
                          <CustomSwitch
                            checked={isEntityAddonEnabled}
                            onChange={(event: any) =>
                              setIsEntityAddonEnabled(event.target.checked)
                            }
                          />
                        }
                        label={
                          isEntityAddonEnabled
                            ? 'Enabled for this database'
                            : 'Disabled for this database'
                        }
                        labelPlacement='start'
                        style={{
                          marginTop: '8px',
                          marginLeft: 0,
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className='screening-divider'></div>
              </>
            )}

            {database.type === 'people & companies' && (
              <div className='screening-settings-col'>
                <div className='hit-title'>
                  <SecurityOutlinedIcon sx={{ fontSize: 14 }} /> Risk Thresholds
                </div>

                <div
                  className='audit-add-modal-cont-body-frame-form'
                  style={{ width: '100%' }}
                >
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                        <div
                          style={{
                            fontSize: '14px',
                            color: 'var(--card-text)',
                            marginTop: '8px',
                            lineHeight: '1.5',
                          }}
                        >
                          <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: 'var(--text-primary)' }}>
                              High Risk:
                            </strong>{' '}
                            {database.type === 'products'
                              ? 'Product HTS Code or Name'
                              : 'Entity Name'}{' '}
                            and Country are matching against risk screening
                            lists, where the proximity to an exact match is
                            greater than 80%.
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: 'var(--text-primary)' }}>
                              Medium Risk:
                            </strong>{' '}
                            {database.type === 'products'
                              ? 'Product'
                              : 'Entity'}{' '}
                            is matching against risk screening lists with a
                            probability greater than 80%, but there is no
                            country match
                          </div>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>
                              Low Risk:
                            </strong>{' '}
                            There is less than an 80% match for the{' '}
                            {database.type === 'products'
                              ? 'product'
                              : 'entity'}
                            and there is no country match
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className='team-content-header'
              style={{ justifyContent: 'flex-end' }}
            >
              <div
                className='create-new-team-btn'
                style={{
                  margin: '0px',
                  border: '1px solid var(--Primary-400, #4895ef)',
                  background: 'var(--Primary-400, #4895ef)',
                }}
                onClick={() => {
                  if (!savingDatabases) {
                    handleSaveSettings();
                  }
                }}
              >
                <div
                  className='create-new-team-btn-text'
                  style={{ display: 'flex', color: 'var(--text-primary)' }}
                >
                  {savingDatabases && (
                    <>
                      <CircularProgress
                        style={{
                          width: '20px',
                          height: '20px',
                          marginRight: '10px',
                        }}
                        color='primary'
                      />
                      ...Saving
                    </>
                  )}
                  {!savingDatabases && 'Save Settings'}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ScreeningSettings;
